/**
 * Integrytas Group - Common Security Module (CSM)
 * First-run bootstrap. Shown only when csm_is_first_run() returns TRUE
 * (i.e. csm_user_master is empty). The auto-provisioning trigger promotes the
 * very first signup to is_admin='Y' and assigns the seed CSM.SUPER_ADMIN role.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APPLICATION_NAME, BRANDING_SUFFIX } from '../../constants/branding';
import type { SignUpInput } from '../../lib/use_auth_session';
import { Watermark } from '../common/Watermark';

export interface FirstAdminSetupScreenProps {
  onCreateAdmin: (input: SignUpInput) => Promise<{ needsEmailConfirmation: boolean }>;
  errorMessage?: string | null;
}

export function FirstAdminSetupScreen({
  onCreateAdmin, errorMessage,
}: FirstAdminSetupScreenProps) {
  const { t } = useTranslation();
  const [email, setEmail]             = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword]       = useState('');
  const [confirm, setConfirm]         = useState('');
  const [busy, setBusy]               = useState(false);
  const [done, setDone]               = useState<null | { needsEmailConfirmation: boolean }>(null);
  const [localError, setLocalError]   = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (password.length < 12) {
      setLocalError(t('auth.first_admin.password_min_error'));
      return;
    }
    if (password !== confirm) {
      setLocalError(t('auth.passwords_dont_match'));
      return;
    }
    setBusy(true);
    try {
      const result = await onCreateAdmin({
        email, password, display_name: displayName || undefined,
      });
      setDone(result);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="relative flex h-screen w-screen items-center justify-center bg-csm-surface-alt">
        <Watermark appName={APPLICATION_NAME} brandingSuffix={BRANDING_SUFFIX} />
        <div className="w-full max-w-md rounded border border-csm-border bg-white p-6 shadow-sm">
          <h1 className="mb-2 text-csm-lg font-semibold text-csm-ink">
            {t('auth.first_admin.done_title')}
          </h1>
          <p className="text-csm-sm text-csm-ink-soft">
            {done.needsEmailConfirmation
              ? t('auth.first_admin.done_needs_confirmation')
              : t('auth.first_admin.done_active')}
          </p>
        </div>
      </div>
    );
  }

  const errMsg = localError || errorMessage;

  return (
    <div className="relative flex h-screen w-screen items-center justify-center bg-csm-surface-alt">
      <div
        aria-hidden
        className="pointer-events-none absolute right-3 top-1 select-none text-csm-xs tracking-wide text-csm-border-strong"
      >
        {APPLICATION_NAME} <span className="font-medium">{BRANDING_SUFFIX}</span>
      </div>

      <form
        onSubmit={submit}
        className="w-full max-w-md rounded border border-csm-border bg-white p-6 shadow-sm"
      >
        <div className="mb-3 rounded border border-csm-warning-border bg-csm-warning-soft px-2 py-1 text-csm-xs uppercase tracking-wider text-csm-warning">
          {t('auth.first_admin.first_run_badge')}
        </div>

        <h1 className="mb-1 text-csm-lg font-semibold text-csm-ink">
          {t('auth.first_admin.title')}
        </h1>
        <p className="mb-4 text-csm-sm text-csm-muted">
          {t('auth.first_admin.subtitle')}
        </p>

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('auth.first_admin.email_label')}</label>
        <input
          type="email" required autoComplete="email"
          value={email} onChange={e => setEmail(e.target.value)}
          className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('auth.first_admin.display_name_label')}</label>
        <input
          type="text" autoComplete="name"
          value={displayName} onChange={e => setDisplayName(e.target.value)}
          className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('auth.first_admin.password_label')}</label>
        <input
          type="password" required autoComplete="new-password" minLength={12}
          value={password} onChange={e => setPassword(e.target.value)}
          className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('auth.first_admin.confirm_password_label')}</label>
        <input
          type="password" required autoComplete="new-password" minLength={12}
          value={confirm} onChange={e => setConfirm(e.target.value)}
          className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />

        {errMsg && (
          <div className="mb-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">
            {errMsg}
          </div>
        )}

        <button
          type="submit" disabled={busy}
          className="h-8 w-full rounded border border-primary bg-primary text-csm-md text-white hover:bg-primary-hover disabled:opacity-60"
        >
          {busy ? t('auth.first_admin.submitting') : t('auth.first_admin.submit')}
        </button>
      </form>
    </div>
  );
}
